import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  cancelBodySystemGenerationJob,
  getBodySystemGenerationJob,
  getBodySystemPrompt,
  getLatestBodySystemGenerationJob,
  revertBodySystemPrompt,
  saveAndGenerateBodySystemPrompt,
  upsertBodySystemPrompt,
} from '../../../api/body-systems';
import { putFile } from '../../../api/fileStorage';
import {
  BodySystemPromptField,
  BodySystemPromptResponse,
  BulkBodySystemGenerationJobPydantic,
  ListBodySystemResponseElement,
  UpsertBodySystemPromptResponse,
} from '../../../types/apiGeneratedTypes';
import Modal from '../../BaseComponents/Modal';
import ConfirmModal from '../../BaseComponents/Modal/ConfirmModal';
import TextArea from '../../BaseComponents/TextArea';
import Button from '../../BaseComponents/Button';
import { ReactComponent as UploadIcon } from '../../../assets/icons/download.svg';
import './EditBodySystemPromptsModal.scss';

type Props = {
  isOpen: boolean;
  bodySystem: ListBodySystemResponseElement;
  field: BodySystemPromptField;
  onClose: () => void;
  onSaved?: () => void | Promise<void>;
};

const fieldLabel = (field: BodySystemPromptField) =>
  field === 'summary' ? 'Summary' : 'Score';

const fileNameFromKey = (key: string) => {
  const parts = key.split('/');
  return parts[parts.length - 1] || key;
};

const JOB_POLL_INTERVAL_MS = 3000;

const isJobActive = (job: BulkBodySystemGenerationJobPydantic | null) =>
  !!job && (job.status === 'queued' || job.status === 'running');

const formatDateTime = (value?: string | null) =>
  value ? new Date(value).toLocaleString('en-GB') : '';

const jobStatusLabel: Record<string, string> = {
  queued: 'Queued',
  running: 'Regenerating',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

type PendingConfirm = 'generate' | 'revert' | null;

const EditBodySystemPromptsModal = ({
  isOpen,
  bodySystem,
  field,
  onClose,
  onSaved,
}: Props) => {
  const [systemPrompt, setSystemPrompt] = useState('');
  const [developerPrompt, setDeveloperPrompt] = useState('');
  const [contextFileKey, setContextFileKey] = useState<string | null>(null);
  const [contextFileUrl, setContextFileUrl] = useState<string | null>(null);
  const [contextFileName, setContextFileName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isReverting, setIsReverting] = useState(false);
  const [isCancellingJob, setIsCancellingJob] = useState(false);
  const [previousVersionCount, setPreviousVersionCount] = useState(0);
  const [job, setJob] = useState<BulkBodySystemGenerationJobPydantic | null>(null);
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const applyPrompt = (
    prompt: BodySystemPromptResponse | UpsertBodySystemPromptResponse
  ) => {
    setSystemPrompt(prompt.system_prompt ?? '');
    setDeveloperPrompt(prompt.developer_prompt ?? '');
    const key = prompt.context_file ?? null;
    setContextFileKey(key);
    setContextFileUrl(prompt.context_file_url ?? null);
    setContextFileName(key ? fileNameFromKey(key) : '');
    setPreviousVersionCount(prompt.previous_version_count ?? 0);
  };

  useEffect(() => {
    if (!isOpen) return;

    const loadPrompt = async () => {
      setIsLoading(true);
      setSystemPrompt('');
      setDeveloperPrompt('');
      setContextFileKey(null);
      setContextFileUrl(null);
      setContextFileName('');
      setPreviousVersionCount(0);
      setJob(null);
      setPendingConfirm(null);
      try {
        const prompt = await getBodySystemPrompt(bodySystem.id, field);
        applyPrompt(prompt);
      } catch {
        // No prompt yet — leave empty form for create via upsert
      }
      try {
        const { job: latestJob } = await getLatestBodySystemGenerationJob(bodySystem.id);
        setJob(latestJob ?? null);
      } catch {
        // Status is informational; the prompt can still be edited without it.
      } finally {
        setIsLoading(false);
      }
    };

    loadPrompt();
  }, [isOpen, bodySystem.id, field]);

  const activeJobId = isOpen && isJobActive(job) ? job?.job_id : undefined;

  // Poll the background job until it finishes.
  useEffect(() => {
    if (!activeJobId) return;

    const intervalId = window.setInterval(async () => {
      try {
        const latest = await getBodySystemGenerationJob(bodySystem.id, activeJobId);
        setJob(latest);
        if (!isJobActive(latest)) {
          const summary = `${latest.generated ?? 0} generated, ${latest.failed ?? 0} failed`;
          if (latest.status === 'completed') {
            toast.success(`Regeneration completed: ${summary}`);
          } else if (latest.status === 'cancelled') {
            toast.info(`Regeneration cancelled: ${summary}`);
          } else {
            toast.error(`Regeneration failed: ${latest.error || summary}`);
          }
        }
      } catch {
        // Keep polling; a single failed status request should not stop progress updates.
      }
    }, JOB_POLL_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [activeJobId, bodySystem.id]);

  const handleAttachmentSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setIsUploading(true);
    try {
      const uploadResponse = await putFile({
        file,
        file_type: file.type || undefined,
      });
      const key = uploadResponse?.full_key as string | undefined;
      if (!key) {
        throw new Error('Upload did not return a file key.');
      }
      setContextFileKey(key);
      setContextFileName(file.name);
      setContextFileUrl(null);
      toast.success('Context file uploaded. Click Save to persist.');
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to upload context file.';
      toast.error(message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveAttachment = () => {
    if (isLocked) return;
    setContextFileKey(null);
    setContextFileUrl(null);
    setContextFileName('');
  };

  const isFormValid = () => {
    if (!systemPrompt.trim() || !developerPrompt.trim()) {
      toast.error('Guidelines and Prompt are required.');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (isBusy) return;
    if (!isFormValid()) return;

    setIsSaving(true);
    try {
      await upsertBodySystemPrompt(bodySystem.id, field, {
        system_prompt: systemPrompt,
        developer_prompt: developerPrompt,
        context_file: contextFileKey,
      });
      toast.success(`${fieldLabel(field)} prompt saved successfully`);
      if (onSaved) {
        await onSaved();
      }
      onClose();
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to save prompt. Please try again.';
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAndGenerate = async () => {
    setPendingConfirm(null);
    if (isBusy) return;
    if (!isFormValid()) return;

    setIsGenerating(true);
    try {
      const response = await saveAndGenerateBodySystemPrompt(bodySystem.id, field, {
        system_prompt: systemPrompt,
        developer_prompt: developerPrompt,
        context_file: contextFileKey,
      });
      applyPrompt(response.prompt);
      setJob(response.job);
      toast.success(
        response.job.total > 0
          ? `${fieldLabel(field)} prompt saved. Regenerating the ${fieldLabel(field).toLowerCase()} for ${response.job.total} members in the background.`
          : `${fieldLabel(field)} prompt saved. No members have results to regenerate.`
      );
      if (onSaved) {
        await onSaved();
      }
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to save prompt and start generation.';
      toast.error(message);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRevert = async () => {
    setPendingConfirm(null);
    if (isBusy) return;

    setIsReverting(true);
    try {
      const prompt = await revertBodySystemPrompt(bodySystem.id, field);
      applyPrompt(prompt);
      toast.success(`${fieldLabel(field)} prompt restored to the previous version`);
      if (onSaved) {
        await onSaved();
      }
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to revert prompt. Please try again.';
      toast.error(message);
    } finally {
      setIsReverting(false);
    }
  };

  const handleCancelJob = async () => {
    if (!job || isCancellingJob) return;

    setIsCancellingJob(true);
    try {
      setJob(await cancelBodySystemGenerationJob(bodySystem.id, job.job_id));
      toast.info('Stopping after the members in progress finish.');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to cancel generation.';
      toast.error(message);
    } finally {
      setIsCancellingJob(false);
    }
  };

  const jobRunning = isJobActive(job);
  const isBusy = isSaving || isUploading || isGenerating || isReverting;
  // The backend refuses prompt changes while results are regenerating.
  const isLocked = isBusy || jobRunning;
  const processed = (job?.generated ?? 0) + (job?.failed ?? 0);
  const progressPercent = job && job.total > 0 ? Math.round((processed / job.total) * 100) : 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${fieldLabel(field)} prompt — ${bodySystem.title}`}
      width="720px"
      height="80vh"
      closeOnEsc
    >
      <div className="edit-bs-prompts-modal">
        {isLoading ? (
          <div className="edit-bs-prompts-loading">Loading prompt…</div>
        ) : (
          <div className="edit-bs-prompts-form">
            {job && (
              <section
                className={`edit-bs-prompts-job edit-bs-prompts-job--${job.status}`}
                aria-live="polite"
              >
                <div className="edit-bs-prompts-job-header">
                  <span className="edit-bs-prompts-job-status">
                    {jobStatusLabel[job.status] ?? job.status}
                    {job.field ? ` — ${fieldLabel(job.field as BodySystemPromptField)}` : ' — Summary and score'}
                  </span>
                  <span className="edit-bs-prompts-job-time">
                    {jobRunning
                      ? `Started ${formatDateTime(job.started_at)}`
                      : `Finished ${formatDateTime(job.finished_at)}`}
                  </span>
                </div>
                <p className="edit-bs-prompts-job-summary">
                  {jobRunning
                    ? `${processed} of ${job.total} members processed (${job.generated ?? 0} generated, ${job.failed ?? 0} failed). Prompts are locked until this finishes.`
                    : `${job.generated ?? 0} of ${job.total} members generated, ${job.failed ?? 0} failed.`}
                </p>
                {jobRunning && (
                  <div
                    className="edit-bs-prompts-job-progress"
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={progressPercent}
                  >
                    <div
                      className="edit-bs-prompts-job-progress-bar"
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                )}
                {job.error && !jobRunning && (job.failed ?? 0) > 0 && (
                  <p className="edit-bs-prompts-job-error">Last error: {job.error}</p>
                )}
                {jobRunning && (
                  <div className="edit-bs-prompts-job-actions">
                    <button
                      type="button"
                      className="edit-bs-prompts-attachment-button edit-bs-prompts-attachment-button--danger"
                      onClick={handleCancelJob}
                      disabled={isCancellingJob}
                    >
                      {isCancellingJob ? 'Stopping…' : 'Stop regeneration'}
                    </button>
                  </div>
                )}
              </section>
            )}

            <TextArea
              title="Guidelines"
              name={`${field}_system_prompt`}
              value={systemPrompt}
              placeholder="Enter guidelines..."
              rows={10}
              isDisabled={isLocked}
              onChange={(event) => setSystemPrompt(event.target.value)}
            />
            <TextArea
              title="Prompt"
              name={`${field}_developer_prompt`}
              value={developerPrompt}
              placeholder="Enter prompt..."
              rows={10}
              isDisabled={isLocked}
              onChange={(event) => setDeveloperPrompt(event.target.value)}
            />

            <section className="edit-bs-prompts-attachment">
              <div className="edit-bs-prompts-attachment-header">
                <h3 className="edit-bs-prompts-attachment-title">Context file</h3>
                <p className="edit-bs-prompts-attachment-help">
                  Upload a file, then click Save. The file is stored in the backend and
                  linked to this prompt.
                </p>
              </div>

              <div className="edit-bs-prompts-attachment-dropzone">
                <input
                  ref={fileInputRef}
                  type="file"
                  className="edit-bs-prompts-attachment-input"
                  onChange={handleAttachmentSelect}
                />

                {contextFileKey ? (
                  <div className="edit-bs-prompts-attachment-meta">
                    <div className="edit-bs-prompts-attachment-file">
                      {contextFileUrl ? (
                        <a
                          className="edit-bs-prompts-attachment-filename"
                          href={contextFileUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {contextFileName || fileNameFromKey(contextFileKey)}
                        </a>
                      ) : (
                        <span className="edit-bs-prompts-attachment-filename">
                          {contextFileName || fileNameFromKey(contextFileKey)}
                        </span>
                      )}
                      <span className="edit-bs-prompts-attachment-key">
                        {contextFileKey}
                      </span>
                    </div>
                    <div className="edit-bs-prompts-attachment-actions">
                      <button
                        type="button"
                        className="edit-bs-prompts-attachment-button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isLocked}
                      >
                        Replace file
                      </button>
                      <button
                        type="button"
                        className="edit-bs-prompts-attachment-button edit-bs-prompts-attachment-button--danger"
                        onClick={handleRemoveAttachment}
                        disabled={isLocked}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="edit-bs-prompts-attachment-empty">
                    <UploadIcon
                      className="edit-bs-prompts-attachment-icon"
                      aria-hidden
                    />
                    <p className="edit-bs-prompts-attachment-empty-text">
                      {isUploading ? 'Uploading…' : 'No context file uploaded.'}
                    </p>
                    <button
                      type="button"
                      className="edit-bs-prompts-attachment-button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isLocked}
                    >
                      Upload file
                    </button>
                  </div>
                )}
              </div>
            </section>

            <div className="edit-bs-prompts-actions">
              <Button
                isSecondary
                text={isReverting ? 'Reverting…' : 'Revert'}
                onClick={() => setPendingConfirm('revert')}
                isDisabled={isLocked || previousVersionCount === 0}
                width="120px"
              />
              <div className="edit-bs-prompts-actions-spacer" />
              <Button
                isSecondary
                text="Cancel"
                onClick={onClose}
                isDisabled={isBusy}
                width="120px"
              />
              <Button
                isSecondary
                text={isSaving ? 'Saving…' : 'Save'}
                onClick={handleSave}
                isDisabled={isLocked}
                width="120px"
              />
              <Button
                text={isGenerating ? 'Starting…' : 'Save and generate'}
                onClick={() => setPendingConfirm('generate')}
                isDisabled={isLocked}
                width="180px"
              />
            </div>
          </div>
        )}
      </div>

      {pendingConfirm === 'generate' && (
        <ConfirmModal
          isOpen
          onClose={() => setPendingConfirm(null)}
          onConfirmClick={handleSaveAndGenerate}
          title="Save and regenerate results?"
          subtitle={`The ${fieldLabel(field).toLowerCase()} prompt will be saved, then the ${bodySystem.title} ${fieldLabel(field).toLowerCase()} will be regenerated for every member with blood results or a scan booking. Their ${field === 'summary' ? 'scores' : 'summaries'} are not changed. This runs in the background and can take a while.`}
          confirmBtnText="Save and generate"
          loading={isGenerating}
        />
      )}
      {pendingConfirm === 'revert' && (
        <ConfirmModal
          isOpen
          onClose={() => setPendingConfirm(null)}
          onConfirmClick={handleRevert}
          title="Restore the previous version?"
          subtitle={`The current ${fieldLabel(field).toLowerCase()} prompt, including any unsaved edits, will be replaced by the version saved before it. Member results are not regenerated.`}
          confirmBtnText="Revert"
          loading={isReverting}
        />
      )}
    </Modal>
  );
};

export default EditBodySystemPromptsModal;
